import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth, getRoleBasePath } from './context/AuthContext';
import './styles/index.css';
import OfflineBanner from './components/shared/OfflineBanner';
import { useOfflineSync } from './hooks/useOfflineSync';
import { usePushNotifications } from './hooks/usePushNotifications';
import { supabase } from './lib/supabase';
import { ClassesProvider } from './context/ClassesContext';
import { GhostProvider } from './context/GhostContext';
import { RequireTeacherPermission } from './context/TeacherPermissionsContext';

// Infra that must always be present — kept eager on purpose. These load on
// every screen regardless of route, so splitting them out would only add
// round-trips with no benefit.
import LoadingScreen from './components/shared/LoadingScreen';
import { isRegistering } from './lib/registrationGuard';
import { NoticeModal } from './components/shared/NoticeModal';

// ─── Lazy-loaded screens ──────────────────────────────────────────
// Everything below is code-split: each screen's JavaScript is downloaded
// only when the user actually navigates to it, instead of shipping the
// entire app (every role's pages) in one bundle up front. This is the
// single biggest win for initial load time and for how heavy the app feels
// on a slow connection. After a screen is opened once its chunk is cached,
// so going back to it is instant.
//
// `lazy()` needs a module with a *default* export. For our components that
// are *named* exports we re-map them to `default` inline with `.then(...)`.

// Auth
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const SchoolSearchPage = lazy(() => import('./pages/auth/SchoolSearchPage'));
const SuspendedPage = lazy(() => import('./pages/auth/SuspendedPage'));
const RegisterSchoolPage = lazy(() => import('./pages/auth/RegisterSchoolPage'));
const ChangePasswordPage = lazy(() => import('./pages/auth/ChangePasswordPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ChangePasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const PendingVerificationPage = lazy(() => import('./pages/auth/ChangePasswordPage').then(m => ({ default: m.PendingVerificationPage })));
const PendingPaymentPage = lazy(() => import('./pages/auth/ChangePasswordPage').then(m => ({ default: m.PendingPaymentPage })));

// Principal
const PrincipalLayout = lazy(() => import('./pages/principal/PrincipalLayout'));
const PrincipalDashboard = lazy(() => import('./pages/principal/PrincipalDashboard'));
const PrincipalSetupWizard = lazy(() => import('./pages/principal/PrincipalSetupWizard'));
const PrincipalStudents = lazy(() => import('./pages/principal/PrincipalStudents'));
const PrincipalRankings = lazy(() => import('./pages/principal/PrincipalRankings'));
const PrincipalClasses = lazy(() => import('./pages/principal/PrincipalClasses'));
const PrincipalAccountDirectory = lazy(() => import('./pages/principal/PrincipalAccountDirectory'));
const PrincipalSchoolLogs = lazy(() => import('./pages/principal/PrincipalSchoolLogs'));
const PrincipalReportSystem = lazy(() => import('./pages/principal/Principalreportsystem'));
const PrincipalFinance = lazy(() => import('./pages/principal/PrincipalFinance'));
const PrincipalExamPlanner = lazy(() => import('./pages/principal/PrincipalExamPlanner'));
const PrincipalPromotion = lazy(() => import('./pages/principal/PrincipalPromotion'));
const PrincipalTeachers = lazy(() => import('./pages/principal/PrincipalTeachers').then(m => ({ default: m.PrincipalTeachers })));
const PrincipalAttendance = lazy(() => import('./pages/principal/PrincipalAttendance').then(m => ({ default: m.PrincipalAttendance })));
const PrincipalEvents = lazy(() => import('./pages/principal/PrincipalEvents').then(m => ({ default: m.PrincipalEvents })));
const PrincipalCourseCoverage = lazy(() => import('./pages/principal/PrincipalCourseCoverage').then(m => ({ default: m.PrincipalCourseCoverage })));
const PrincipalAdmissions = lazy(() => import('./pages/principal/PrincipalAdmissions').then(m => ({ default: m.PrincipalAdmissions })));
const PrincipalStudyCenter = lazy(() => import('./pages/principal/PrincipalStudyCenter').then(m => ({ default: m.PrincipalStudyCenter })));
const PrincipalSubscription = lazy(() => import('./pages/principal/PrincipalSubscription').then(m => ({ default: m.PrincipalSubscription })));
const PrincipalAnnouncements = lazy(() => import('./pages/principal/PrincipalAnnouncements').then(m => ({ default: m.PrincipalAnnouncements })));
const PrincipalHomework = lazy(() => import('./pages/principal/PrincipalHomework').then(m => ({ default: m.PrincipalHomework })));
const PrincipalTimetable = lazy(() => import('./pages/principal/PrincipalTimetable').then(m => ({ default: m.PrincipalTimetable })));
const PrincipalStaffRoles = lazy(() => import('./pages/principal/PrincipalStaffRoles').then(m => ({ default: m.PrincipalStaffRoles })));

// Teacher
const TeacherLayout = lazy(() => import('./pages/teacher/TeacherLayout'));
const TeacherStudents = lazy(() => import('./pages/teacher/TeacherStudents'));
const TeacherDashboard = lazy(() => import('./pages/teacher/TeacherPages').then(m => ({ default: m.TeacherDashboard })));
const TeacherAttendance = lazy(() => import('./pages/teacher/TeacherPages').then(m => ({ default: m.TeacherAttendance })));
const TeacherScores = lazy(() => import('./pages/teacher/TeacherPages').then(m => ({ default: m.TeacherScores })));
const TeacherHomework = lazy(() => import('./pages/teacher/TeacherPages').then(m => ({ default: m.TeacherHomework })));
const TeacherCourseCoverage = lazy(() => import('./pages/teacher/TeacherPages').then(m => ({ default: m.TeacherCourseCoverage })));
const TeacherRankings = lazy(() => import('./pages/teacher/TeacherPages').then(m => ({ default: m.TeacherRankings })));
const TeacherReports = lazy(() => import('./pages/teacher/TeacherPages').then(m => ({ default: m.TeacherReports })));
const TeacherTimetable = lazy(() => import('./pages/teacher/TeacherPages').then(m => ({ default: m.TeacherTimetable })));
const TeacherExamPlanner = lazy(() => import('./pages/teacher/TeacherPages').then(m => ({ default: m.TeacherExamPlanner })));

// Parent
const ParentLayout = lazy(() => import('./pages/parent/ParentLayout').then(m => ({ default: m.ParentLayout })));
const ParentDashboard = lazy(() => import('./pages/parent/ParentDashboard').then(m => ({ default: m.ParentDashboard })));
const ParentFees = lazy(() => import('./pages/parent/ParentFees').then(m => ({ default: m.ParentFees })));
const ParentEvents = lazy(() => import('./pages/parent/ParentEvents').then(m => ({ default: m.ParentEvents })));
const ParentPastPapers = lazy(() => import('./pages/parent/ParentPastPapers').then(m => ({ default: m.ParentPastPapers })));
const ParentPerformance = lazy(() => import('./pages/parent/ParentPerformance').then(m => ({ default: m.ParentPerformance })));
const ParentHomework = lazy(() => import('./pages/parent/ParentHomework').then(m => ({ default: m.ParentHomework })));
const ParentAttendance = lazy(() => import('./pages/parent/ParentAttendance').then(m => ({ default: m.ParentAttendance })));
const ParentExams = lazy(() => import('./pages/parent/ParentExams').then(m => ({ default: m.ParentExams })));
const ParentAcademicHistory = lazy(() => import('./pages/parent/ParentAcademicHistory').then(m => ({ default: m.ParentAcademicHistory })));
const ParentCourseCoverage = lazy(() => import('./pages/parent/ParentCourseCoverage').then(m => ({ default: m.ParentCourseCoverage })));
const ParentTimetable = lazy(() => import('./pages/parent/ParentTimetable').then(m => ({ default: m.ParentTimetable })));
const ParentRankings = lazy(() => import('./pages/parent/ParentRankings').then(m => ({ default: m.ParentRankings })));
const ParentReport = lazy(() => import('./pages/parent/ParentReport'));
const ParentLoginPage = lazy(() => import('./pages/parent/ParentLoginPage'));
const ParentSignupPage = lazy(() => import('./pages/parent/ParentSignupPage'));

// Student
const StudentLayout = lazy(() => import('./pages/student/StudentLayout').then(m => ({ default: m.StudentLayout })));
const StudentDashboard = lazy(() => import('./pages/student/StudentDashboard').then(m => ({ default: m.StudentDashboard })));
const StudentReport = lazy(() => import('./pages/student/StudentReport').then(m => ({ default: m.StudentReport })));
const StudentExams = lazy(() => import('./pages/student/StudentExams').then(m => ({ default: m.StudentExams })));
const StudentCourseCoverage = lazy(() => import('./pages/student/StudentCourseCoverage').then(m => ({ default: m.StudentCourseCoverage })));
const StudentHomework = lazy(() => import('./pages/student/StudentHomework').then(m => ({ default: m.StudentHomework })));
const StudentAcademicHistory = lazy(() => import('./pages/student/StudentAcademicHistory').then(m => ({ default: m.StudentAcademicHistory })));
const StudentRankings = lazy(() => import('./pages/student/StudentRankings').then(m => ({ default: m.StudentRankings })));
const StudentAttendance = lazy(() => import('./pages/student/StudentAttendance').then(m => ({ default: m.StudentAttendance })));
const StudentTimetable = lazy(() => import('./pages/student/StudentTimetable').then(m => ({ default: m.StudentTimetable })));

// Admin
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminVerificationQueue = lazy(() => import('./pages/admin/AdminVerificationQueue'));
const AdminSchools = lazy(() => import('./pages/admin/AdminSchools'));
const AdminSubscriptions = lazy(() => import('./pages/admin/AdminSubscriptions'));
const AdminOmniMatrix = lazy(() => import('./pages/admin/AdminOmniMatrix'));
const AdminEngagement = lazy(() => import('./pages/admin/AdminEngagement').then(m => ({ default: m.AdminEngagement })));
const AdminNotices = lazy(() => import('./pages/admin/AdminNotices'));
const AdminSessions = lazy(() => import('./pages/admin/AdminSessions'));

// Shared
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// ─── Guards ───────────────────────────────────────────────────
function ProtectedRoute({ children, allowedRoles }: { children: React.ReactNode; allowedRoles?: string[] }) {
  const { user, school, loading, initialized } = useAuth();
  const location = useLocation();

  if (!initialized || loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;

 if (user.role !== 'super_admin' && user.role !== 'parent') {
    if (!school) return <LoadingScreen />;
    if (school.subscription_status === 'suspended') return <Navigate to="/suspended" replace />;
    if (school.subscription_status === 'pending_verification' && user.role !== 'principal') return <Navigate to="/pending-verification" replace />;
    if (school.subscription_status === 'pending_payment') return <Navigate to="/pending-payment" replace />;
  }

  if (user.role === 'principal' && school && !school.setup_complete && !location.pathname.includes('/setup')) {
    return <Navigate to="/principal/setup" replace />;
  }

  if (!user.default_password_changed && user.role !== 'super_admin' && !location.pathname.includes('/change-password')) {
    return <Navigate to="/change-password" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Super admin in ghost mode: allow through to any role's pages
    if (user.role === 'super_admin') {
      console.log('[Ghost] super_admin bypassing role guard for:', allowedRoles);
    } else {
      return <Navigate to={getRoleBasePath(user.role)} replace />;
    }
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, initialized } = useAuth();
  if (!initialized || loading) return <LoadingScreen />;
  // While a school registration is mid-flight there can be a live session with
  // no profile row yet. Redirecting here would throw the principal off the
  // credentials screen before they've copied their username — which is
  // unrecoverable, since the username is only shown once. See
  // lib/registrationGuard.ts.
  if (user && !isRegistering()) return <Navigate to={getRoleBasePath(user.role)} replace />;
  return <>{children}</>;
}

// ─── Routes ───────────────────────────────────────────────────
function AppRoutes() {
  usePushNotifications();
  const offlineSync = useOfflineSync(supabase);

  return (
    <>
      <OfflineBanner
        isOnline={offlineSync.isOnline}
        pendingCount={offlineSync.pendingCount}
        isSyncing={offlineSync.isSyncing}
        lastSyncAt={offlineSync.lastSyncAt}
        onManualSync={offlineSync.flushQueue}
      />

      {/* App-wide: Notices/Announcements are broadcast from the admin
          portal out to every school, so this has to live above the role
          routes, not inside AdminLayout — any logged-in role can be shown
          one. useActiveNotices (inside NoticeModal) already no-ops when
          there's no logged-in user. */}
      <NoticeModal />

      {/* Suspense catches every lazy screen while its chunk downloads and
          shows the same LoadingScreen the guards use, so a first visit to a
          screen looks identical to today's load state — just lighter. */}
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          <Route path="/" element={<PublicRoute><SchoolSearchPage /></PublicRoute>} />
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterSchoolPage /></PublicRoute>} />
          <Route path="/suspended" element={<SuspendedPage />} />
          <Route path="/pending-verification" element={<PendingVerificationPage />} />
          <Route path="/pending-payment" element={<PendingPaymentPage />} />
          <Route path="/change-password" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />

          {/* ── Principal ──────────────────────────────────────── */}
          <Route path="/principal" element={<ProtectedRoute allowedRoles={['principal']}><PrincipalLayout /></ProtectedRoute>}>
            <Route index element={<PrincipalDashboard />} />
            <Route path="setup" element={<PrincipalSetupWizard />} />
            <Route path="classes" element={<PrincipalClasses />} />
            <Route path="students" element={<PrincipalStudents />} />
            <Route path="teachers" element={<PrincipalTeachers />} />
            <Route path="attendance" element={<PrincipalAttendance />} />
            <Route path="rankings" element={<PrincipalRankings />} />
            <Route path="fees" element={<PrincipalFinance />} />
            <Route path="/principal/reports" element={<PrincipalReportSystem />} />
            <Route path="events" element={<PrincipalEvents />} />
            <Route path="exams" element={<PrincipalExamPlanner />} />
            <Route path="coverage" element={<PrincipalCourseCoverage />} />
            <Route path="admissions" element={<PrincipalAdmissions />} />
            <Route path="study-center" element={<PrincipalStudyCenter />} />
            <Route path="subscription" element={<PrincipalSubscription />} />
            <Route path="promotion" element={<PrincipalPromotion />} />
            {/* Section 6 — Account Directory */}
            <Route path="accounts" element={<PrincipalAccountDirectory />} />
            <Route path="roles" element={<PrincipalStaffRoles />} />
            <Route path="announcements" element={<PrincipalAnnouncements />} />
            <Route path="homework" element={<PrincipalHomework />} />
            {/* PrincipalAcademicHistory was retired in favor of PrincipalStudents,
                which now covers the same ground (see PrincipalStudents.tsx).
                Keep this route so old links/bookmarks to /principal/history
                don't 404. */}
            <Route path="history" element={<PrincipalStudents />} />
            <Route path="logs" element={<PrincipalSchoolLogs />} />
            <Route path="timetable" element={<PrincipalTimetable />} />
          </Route>

          {/* ── Teacher ────────────────────────────────────────── */}
          <Route path="/teacher" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherLayout /></ProtectedRoute>}>
            <Route index element={<TeacherDashboard />} />
            <Route path="students" element={<TeacherStudents />} />
            {/* TeacherAcademicHistory was retired in favor of TeacherStudents,
                which now covers the same ground (see TeacherStudents.tsx).
                Keep this route so old links/bookmarks to /teacher/history
                don't 404. */}
            <Route path="history" element={<TeacherStudents />} />
            <Route path="attendance" element={<TeacherAttendance />} />
            <Route path="scores" element={<TeacherScores />} />
            <Route path="homework" element={<TeacherHomework />} />
            <Route path="coverage" element={<TeacherCourseCoverage />} />
            <Route path="exams" element={<TeacherExamPlanner />} />
            <Route path="rankings" element={<TeacherRankings />} />
            <Route path="reports" element={<TeacherReports />} />
            <Route path="timetable" element={<TeacherTimetable />} />
            <Route path="report-system" element={<RequireTeacherPermission permissionKey="reports"><PrincipalReportSystem /></RequireTeacherPermission>} />
            <Route path="timetable-manager" element={<RequireTeacherPermission permissionKey="timetable"><PrincipalTimetable /></RequireTeacherPermission>} />
            <Route path="coverage-manager" element={<RequireTeacherPermission permissionKey="coverage"><PrincipalCourseCoverage /></RequireTeacherPermission>} />
            <Route path="announcements" element={<RequireTeacherPermission permissionKey="announcements"><PrincipalAnnouncements /></RequireTeacherPermission>} />
            <Route path="events" element={<RequireTeacherPermission permissionKey="events"><PrincipalEvents /></RequireTeacherPermission>} />
            <Route path="accounts" element={<RequireTeacherPermission permissionKey="accounts"><PrincipalAccountDirectory /></RequireTeacherPermission>} />
            <Route path="fees" element={<RequireTeacherPermission permissionKey="fees"><PrincipalFinance /></RequireTeacherPermission>} />
          </Route>

          {/* ── Parent public routes ───────────────────────────── */}
          <Route path="/parent/login" element={<PublicRoute><ParentLoginPage /></PublicRoute>} />
          <Route path="/parent/signup" element={<PublicRoute><ParentSignupPage /></PublicRoute>} />

          {/* ── Parent protected routes ────────────────────────── */}
          <Route path="/parent" element={<ProtectedRoute allowedRoles={['parent']}><ParentLayout /></ProtectedRoute>}>
            <Route index element={<ParentDashboard />} />
            <Route path="timetable" element={<ParentTimetable />} />
            <Route path="homework" element={<ParentHomework />} />
            <Route path="report" element={<ParentReport />} />
            <Route path="rankings" element={<ParentRankings />} />
            <Route path="exams" element={<ParentExams />} />
            <Route path="coverage" element={<ParentCourseCoverage />} />
            <Route path="history" element={<ParentAcademicHistory />} />
            <Route path="attendance" element={<ParentAttendance />} />
            <Route path="fees" element={<ParentFees />} />
            <Route path="events" element={<ParentEvents />} />
            <Route path="past-papers" element={<ParentPastPapers />} />
            <Route path="/parent/performance" element={<ParentPerformance />} />
          </Route>

          {/* ── Student ────────────────────────────────────────── */}
          <Route path="/student" element={<ProtectedRoute allowedRoles={['student']}><StudentLayout /></ProtectedRoute>}>
            <Route index element={<StudentDashboard />} />
            <Route path="timetable" element={<StudentTimetable />} />
            <Route path="report" element={<StudentReport />} />
            <Route path="exams" element={<StudentExams />} />
            <Route path="coverage" element={<StudentCourseCoverage />} />
            <Route path="homework" element={<StudentHomework />} />
            <Route path="attendance" element={<StudentAttendance />} />
            <Route path="history" element={<StudentAcademicHistory />} />
            <Route path="rankings" element={<StudentRankings />} />
          </Route>

          {/* ── Admin ──────────────────────────────────────────── */}
          <Route path="/admin" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminLayout /></ProtectedRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="verification" element={<AdminVerificationQueue />} />
            <Route path="schools" element={<AdminSchools />} />
            <Route path="subscriptions" element={<AdminSubscriptions />} />
            <Route path="omni-matrix" element={<AdminOmniMatrix />} />
            <Route path="engagement" element={<AdminEngagement />} />
            <Route path="notices" element={<AdminNotices />} />
            <Route path="sessions" element={<AdminSessions />} />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <GhostProvider>
          <ClassesProvider>
            <AppRoutes />
          </ClassesProvider>
        </GhostProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
